import { css } from 'aphrodite';
import { ReactNode } from 'react';
import { UserRole } from '../../../admin/containers/account/accountTabs/Settings/options';
import { ReviewStatus, TemplateReviewForCard, UserSessionContext } from '../../../extViewmodels';
import { bs } from '../../styles/styles';
import { ApproveMarkIcon } from '../svgs/icons/ApproveMarkIcon';
import { RejectedMarkIcon } from '../svgs/icons/RejectedMarkIcon/RejectedMarkIcon';
import { getIndicatorStyle, iconStyle, styles } from './styles';
import { TemplateApprovalStatusProps, TemplateReviewStatusIndicatorProps } from './types';

/**
 * ApprovalStatus component to display the approval or rejection status
 */
export const TemplateReviewStatusIndicator: React.FC<TemplateReviewStatusIndicatorProps> = ({
	isApproved,
	noMargin = true,
	displayText,
}) => {
	const style = getIndicatorStyle({ isApproved, noMargin });

	return (
		<span className={css(bs.ml3, bs.fontBold, styles.indicator)} style={style}>
			{isApproved ? (
				<ApproveMarkIcon style={iconStyle} color={style.color} />
			) : (
				<RejectedMarkIcon style={iconStyle} color={style.color} />
			)}
			{displayText}
		</span>
	);
};

/**
 * Returns the approval status component based on the template review status
 */
export function getTemplateApprovalStatus(templateReview?: TemplateReviewForCard, noMargin?: boolean): ReactNode;
export function getTemplateApprovalStatus(props: TemplateApprovalStatusProps): ReactNode;
export function getTemplateApprovalStatus(
	propsOrTemplateReview?: TemplateReviewForCard | TemplateApprovalStatusProps,
	noMarginParam?: boolean
): ReactNode {
	let templateReview: TemplateReviewForCard | undefined;
	let noMargin: boolean | undefined;

	if (propsOrTemplateReview && 'templateReview' in propsOrTemplateReview) {
		templateReview = propsOrTemplateReview.templateReview;
		noMargin = propsOrTemplateReview.noMargin;
	} else {
		templateReview = propsOrTemplateReview as TemplateReviewForCard | undefined;
		noMargin = noMarginParam;
	}

	if (!templateReview) {
		return null;
	}
	if (templateReview.status === ReviewStatus.PreApproved || templateReview.status === ReviewStatus.PreReviewed) {
		return (
			<TemplateReviewStatusIndicator
				isApproved={true}
				noMargin={noMargin}
				displayText={templateReview.displayText ?? 'Approved'}
			/>
		);
	} else if (templateReview.status === ReviewStatus.Rejected) {
		return (
			<TemplateReviewStatusIndicator
				isApproved={false}
				noMargin={noMargin}
				displayText={templateReview.displayText ?? 'Rejected'}
			/>
		);
	}
	return null;
}

export const hasTemplateReviewCapability = (userSession: UserSessionContext): boolean => {
	const selfService =
		(userSession.account.features.templateReviewSettings?.preApproved?.enabled ||
			userSession.account.features.templateReviewSettings?.preReviewed?.enabled ||
			userSession.account.features.templateReviewSettings?.rejected?.enabled) &&
		userSession.userRole === UserRole.SuperAdmin;
	return selfService || userSession.account.isBrokerDealer;
};
